import type { CSSProperties } from "react";

import { cn } from "../utils";

export interface ProgressRingSegment {
  /** Share of the ring, 0..1. Segments are laid clockwise from twelve in order. */
  value: number;
  /** Start position, 0..1, instead of following the previous segment. Lets a later (topmost) segment overlay an earlier one. */
  offset?: number;
  /** Overrides the ring's `linecap` for this segment. */
  linecap?: "butt" | "round";
  /** Dash along the stroke instead of a solid arc (draft provenance). */
  dashed?: boolean;
  className?: string;
  style?: CSSProperties;
}

export interface ProgressRingProps {
  /** 0..1, clamped. Shorthand for a single segment styled by `arcClassName` / `arcStyle`. */
  value?: number;
  /** Several arcs laid end to end; wins over `value`. */
  segments?: ProgressRingSegment[];
  /** Diameter in px. */
  size: number;
  /** Ring thickness in px. */
  strokeWidth: number;
  /** Tailwind `stroke-*` class for the unfilled track. */
  trackClassName?: string;
  /** Tailwind `stroke-*` class for the filled arc. */
  arcClassName?: string;
  /** Escape hatch for callers whose colour is computed, not a token. */
  trackStyle?: CSSProperties;
  arcStyle?: CSSProperties;
  linecap?: "butt" | "round";
  /** Dashes the track, for "not yet" states that should not read as an empty score. */
  trackDashed?: boolean;
  /**
   * Visible empty arc in px between adjacent segments that differ in colour
   * or minted/draft texture. Those boundaries get round caps. No-op for a
   * single visible segment.
   */
  gap?: number;
  className?: string;
}

function segmentKey(segment: { className?: string; dashed?: boolean }): string {
  const stroke = (segment.className ?? "")
    .split(/\s+/)
    .filter((token) => token.startsWith("stroke-"))
    .join(" ");
  return `${stroke}:${segment.dashed ? "draft" : "minted"}`;
}

function arcPath(center: number, radius: number, offset: number, value: number): string {
  const start = -Math.PI / 2 + offset * 2 * Math.PI;
  const end = start + value * 2 * Math.PI;
  const x1 = center + radius * Math.cos(start);
  const y1 = center + radius * Math.sin(start);
  const x2 = center + radius * Math.cos(end);
  const y2 = center + radius * Math.sin(end);
  const large = value > 0.5 ? 1 : 0;
  return `M ${x1.toFixed(3)} ${y1.toFixed(3)} A ${radius.toFixed(3)} ${radius.toFixed(3)} 0 ${large} 1 ${x2.toFixed(3)} ${y2.toFixed(3)}`;
}

// One dash unit for every draft arc, so dashes never differ in size or
// weight between segments; only their count does.
const DASH_RATIO = 0.75;
const DASH_GAP_RATIO = 0.5;
const MIN_DASHES = 2;

interface LaidSegment extends ProgressRingSegment {
  offset: number;
}

interface DashedArc {
  count: number;
  dash: number;
  gap: number;
}

/**
 * Every visible segment is guaranteed room for `MIN_DASHES` dashes (or the
 * same length solid), so a one-in-eight status never collapses to a sliver.
 * The remainder is split by share. When even the floors do not fit, all
 * segments share the ring equally: the tooltip carries the exact counts.
 */
function layoutSegments(
  segments: ProgressRingSegment[],
  circumference: number,
  gapPx: number,
  strokeWidth: number,
): LaidSegment[] {
  let cursor = 0;
  const laid: LaidSegment[] = [];
  for (const segment of segments) {
    const value = Math.min(1, Math.max(0, segment.value));
    if (value <= 0) continue;
    const offset = segment.offset ?? cursor;
    const prev = laid[laid.length - 1];
    // A round cap cannot be applied to one end only, so two look-alike
    // neighbours become one arc rather than a cap spilling into the other.
    if (
      gapPx > 0 &&
      prev &&
      segmentKey(prev) === segmentKey(segment) &&
      segment.offset === undefined
    ) {
      prev.value += value;
    } else {
      laid.push({ ...segment, value, offset });
    }
    cursor = offset + value;
  }
  if (gapPx <= 0 || laid.length < 2) return laid;

  const total = laid.reduce((sum, segment) => sum + segment.value, 0);
  const closed = total >= 1 - 1e-6;
  const boundaryPx = gapPx + strokeWidth;
  const boundaries = laid.filter((segment, index) => {
    const prev = laid[(index - 1 + laid.length) % laid.length]!;
    return (index > 0 || closed) && segmentKey(prev) !== segmentKey(segment);
  }).length;
  const spanPx = circumference * total;
  const availablePx = Math.max(0, spanPx - boundaries * boundaryPx);
  const dash = strokeWidth * DASH_RATIO;
  const dashGap = strokeWidth * DASH_GAP_RATIO;
  const floorPx = MIN_DASHES * dash + (MIN_DASHES - 1) * dashGap;
  const floorsFit = availablePx >= laid.length * floorPx;
  const flexPx = floorsFit ? availablePx - laid.length * floorPx : 0;

  let position = laid[0]!.offset * circumference;
  return laid.map((segment, index) => {
    const prev = laid[(index - 1 + laid.length) % laid.length]!;
    const gapBefore = (index > 0 || closed) && segmentKey(prev) !== segmentKey(segment);
    const next = laid[(index + 1) % laid.length]!;
    const gapAfter =
      (index < laid.length - 1 || closed) && segmentKey(next) !== segmentKey(segment);
    const lengthPx = floorsFit
      ? floorPx + (flexPx * segment.value) / total
      : availablePx / laid.length;
    const round = gapBefore || gapAfter;
    // The round cap adds half a stroke past each path end; an open end of an
    // unclosed ring pulls back by that much so the cap stops where a butt would.
    const capBefore = round && !gapBefore ? strokeWidth / 2 : 0;
    const capAfter = round && !gapAfter ? strokeWidth / 2 : 0;
    if (gapBefore) position += boundaryPx / 2;
    const offset = (position + capBefore) / circumference;
    position += lengthPx;
    if (gapAfter) position += boundaryPx / 2;
    return {
      ...segment,
      offset,
      value: Math.max(0, lengthPx - capBefore - capAfter) / circumference,
      ...(round ? { linecap: "round" as const } : {}),
    };
  });
}

/** Whole dashes of one size; a lone full ring stretches the unit slightly so it closes. */
function dashedArc(lengthPx: number, strokeWidth: number, full: boolean): DashedArc {
  const dash = strokeWidth * DASH_RATIO;
  const gap = strokeWidth * DASH_GAP_RATIO;
  const period = dash + gap;
  if (full) {
    const count = Math.max(MIN_DASHES, Math.round(lengthPx / period));
    const scale = lengthPx / (count * period);
    return { count, dash: dash * scale, gap: gap * scale };
  }
  // Fewer than MIN_DASHES only when the layout fell back to equal slots.
  const count = Math.max(1, Math.floor((lengthPx + gap) / period + 1e-6));
  return { count, dash: Math.min(dash, lengthPx), gap };
}

/**
 * The bare ring: a track with an arc sweeping clockwise from twelve. Carries
 * no meaning of its own; `ConfidenceIndicatorRing` and `EvalScoreCircle` add
 * the colour scale, label and tooltip.
 */
export function ProgressRing({
  value = 0,
  segments,
  size,
  strokeWidth,
  trackClassName,
  arcClassName,
  trackStyle,
  arcStyle,
  linecap = "butt",
  trackDashed = false,
  gap = 0,
  className,
}: ProgressRingProps) {
  const center = size / 2;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const fallback: ProgressRingSegment = {
    value,
    ...(arcClassName ? { className: arcClassName } : {}),
    ...(arcStyle ? { style: arcStyle } : {}),
  };
  const arcs = layoutSegments(segments ?? [fallback], circumference, gap, strokeWidth);

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      aria-hidden="true"
      className={cn("block shrink-0", className)}
    >
      <circle
        cx={center}
        cy={center}
        r={radius}
        fill="none"
        strokeWidth={strokeWidth}
        strokeDasharray={trackDashed ? `${strokeWidth} ${strokeWidth}` : undefined}
        className={trackClassName}
        style={trackStyle}
      />
      {arcs.map((arc, index) => {
        if (arc.dashed) {
          const full = arc.value >= 1 - 1e-6;
          const lengthPx = circumference * arc.value;
          const { count, dash, gap: dashGap } = dashedArc(lengthPx, strokeWidth, full);
          const dashProps = {
            fill: "none" as const,
            strokeWidth,
            strokeLinecap: "butt" as const,
            strokeDasharray: `${dash} ${dashGap}`,
            className: arc.className,
            style: arc.style,
            "data-dashed": "",
            "data-dash-count": count,
          };
          if (full) {
            return (
              <circle
                key={index}
                cx={center}
                cy={center}
                r={radius}
                transform={`rotate(-90 ${center} ${center})`}
                {...dashProps}
              />
            );
          }
          // Centre the whole dashes inside the slot so the arc ends on a dash.
          const drawnPx = count * dash + (count - 1) * dashGap;
          const inset = Math.max(0, lengthPx - drawnPx) / 2 / circumference;
          return (
            <path
              key={index}
              d={arcPath(center, radius, arc.offset + inset, drawnPx / circumference)}
              {...dashProps}
            />
          );
        }
        return (
          <circle
            key={index}
            cx={center}
            cy={center}
            r={radius}
            fill="none"
            strokeWidth={strokeWidth}
            strokeLinecap={arc.linecap ?? linecap}
            strokeDasharray={`${circumference * arc.value} ${circumference}`}
            transform={`rotate(${-90 + arc.offset * 360} ${center} ${center})`}
            className={arc.className}
            style={arc.style}
          />
        );
      })}
    </svg>
  );
}
